import { ConvertUOMs } from "../../global";

const defaultUom = {
  uoMName: "g",
  type: "weight",
  metricStandardUoMName: "g",
  metricStandardConversion: 1,
};

export function getConvertedCurrentAmount(oWorkRequestSample, isUpdate, samplesSameKey = [], options = {}) {
  const siteName = options?.siteName ?? null;
  const locations = options?.locations ?? [];
  const requireSiteForAny = options?.requireSiteForAny ?? false;

  // If no site selected and this is "Any" container, optionally return empty (caller-controlled)
  if (requireSiteForAny && !siteName && !oWorkRequestSample?.existingContainerID) {
    return "";
  }
  if (isUpdate) {
    const selectedContainerWithValidCurrentAmountToDisplay =
      oWorkRequestSample &&
      oWorkRequestSample.existingContainer &&
      oWorkRequestSample.existingContainer.currentAmount &&
      oWorkRequestSample.existingContainer.containerSizeUoM;

    if (selectedContainerWithValidCurrentAmountToDisplay) {
      const validDataToDoConversion =
        oWorkRequestSample.sample &&
        oWorkRequestSample.sample.substance &&
        oWorkRequestSample.unitOfMeasure;

      if (oWorkRequestSample.requestedAmountUoM && validDataToDoConversion) {
        const sample = oWorkRequestSample.sample;
        const substance = oWorkRequestSample.sample.substance;
        const currentAmount =
          oWorkRequestSample.existingContainer.currentAmount;
        const originalUoM = oWorkRequestSample.existingContainer.uom;
        const convertToUoM = oWorkRequestSample.unitOfMeasure;

        const newConvertedCurrentAmount = ConvertUOMs(
          sample,
          substance,
          currentAmount,
          originalUoM,
          convertToUoM
        );

        if (typeof newConvertedCurrentAmount === "number") {
          return `${
            newConvertedCurrentAmount % 1
              ? newConvertedCurrentAmount.toFixed(4)
              : newConvertedCurrentAmount
          } ${oWorkRequestSample.requestedAmountUoM}`;
        }
      }
      return `${oWorkRequestSample.existingContainer.currentAmount} ${oWorkRequestSample.existingContainer.containerSizeUoM}`;
    }
  } else {
    const validForConversion =
      oWorkRequestSample &&
      oWorkRequestSample.sample &&
      oWorkRequestSample.sample.containers &&
      oWorkRequestSample.sample.containers.length > 0;

    if (validForConversion) {
      const isConfirmedContainer = (container) =>
        container?.containerStatusName === "Confirmed" ||
        container?.containerStatus?.name === "Confirmed";

      const hasNonNegativeAmount = (container) => {
        const amount = Number(container?.currentAmount);
        return Number.isFinite(amount) && amount >= 0;
      };

      const getContainerSiteName = (container) =>
        container?.siteName ??
        container?.location?.siteName ??
        locations.find((location) => location.locationName === container?.locationName)?.siteName ??
        null;

      const container = oWorkRequestSample.sample.containers.find(
        (c) => c.id === oWorkRequestSample.existingContainerID
      );

      if (container) {
        const validDataToDoConversion =
          oWorkRequestSample.sample &&
          oWorkRequestSample.sample.substance &&
          oWorkRequestSample.unitOfMeasure;

        if (oWorkRequestSample.requestedAmountUoM && validDataToDoConversion) {
          const sample = oWorkRequestSample.sample;
          const substance = oWorkRequestSample.sample.substance;
          const currentAmount = container.currentAmount;
          const originalUoM = container.uom;
          const convertToUoM = oWorkRequestSample.unitOfMeasure;

          let newConvertedCurrentAmount = ConvertUOMs(
            sample,
            substance,
            currentAmount,
            originalUoM,
            convertToUoM
          );

          if (typeof newConvertedCurrentAmount === "number") {
            samplesSameKey.forEach(item => {
              const requestedAmount = item.requestedAmount ? parseFloat(item.requestedAmount) : 0;
              newConvertedCurrentAmount -= requestedAmount;
            })
            return `${
              newConvertedCurrentAmount % 1
                ? newConvertedCurrentAmount.toFixed(4)
                : newConvertedCurrentAmount
            } ${oWorkRequestSample.requestedAmountUoM}`;
          }
        }
        return `${container.currentAmount} ${container.containerSizeUoM}`;
      } else {
        const sample = oWorkRequestSample.sample;
        const substance = oWorkRequestSample.sample.substance;
        const convertToUoM = oWorkRequestSample.unitOfMeasure ?? null;

        let fullValue = oWorkRequestSample.sample.containers.reduce(
          (accumulator, currentValue) => {
            if (!isConfirmedContainer(currentValue)) {
              return accumulator;
            }

            if (!hasNonNegativeAmount(currentValue)) {
              return accumulator;
            }

            if (siteName && getContainerSiteName(currentValue) !== siteName) {
              return accumulator;
            }

            const newConvertedCurrentAmount = ConvertUOMs(
              sample,
              substance,
              currentValue.currentAmount,
              currentValue.uom,
              convertToUoM
            );

            return accumulator + newConvertedCurrentAmount;
          },
          0
        );

        if (typeof fullValue === "number") {
          samplesSameKey.forEach(item => {
            const requestedAmount = item.requestedAmount ? parseFloat(item.requestedAmount) : 0;
            fullValue -= requestedAmount;
          })
          return `${fullValue % 1 ? fullValue.toFixed(4) : fullValue} ${
            oWorkRequestSample.requestedAmountUoM ?? defaultUom.uoMName
          }`;
        }
      }
    }
  }
  return "";
}

export function getUpdatedWorkRequestsWhenChangingRequestedAmountUoM(
  myWorkRequest,
  index,
  value
) {
  return myWorkRequest.workRequestSamples.map((sample, idx) => {
    if (index === idx) {
      return {
        ...sample,
        requestedAmountUoM: value.uoMName,
        unitOfMeasure: value,
      };
    }
    return sample;
  });
}

export const getOriginalAmount = (myContainer) => {
  // Get original container amount without any subtractions
  const validForConversion =
    myContainer &&
    myContainer.sample &&
    myContainer.sample.containers &&
    myContainer.sample.containers.length > 0;

  if (validForConversion) {
    const container = myContainer.sample.containers.find(
      (c) => c.id === myContainer.existingContainerID
    );

    if (container) {
      const validDataToDoConversion =
        myContainer.sample &&
        myContainer.sample.substance &&
        myContainer.unitOfMeasure;

      if (myContainer.requestedAmountUoM && validDataToDoConversion) {
        const sample = myContainer.sample;
        const substance = myContainer.sample.substance;
        const currentAmount = container.currentAmount;
        const originalUoM = container.uom;
        const convertToUoM = myContainer.unitOfMeasure;

        const convertedAmount = ConvertUOMs(
          sample,
          substance,
          currentAmount,
          originalUoM,
          convertToUoM
        );

        if (typeof convertedAmount === "number") {
          return `${
            convertedAmount % 1
              ? convertedAmount.toFixed(4)
              : convertedAmount
          } ${myContainer.requestedAmountUoM}`;
        }
      }
      return `${container.currentAmount} ${container.containerSizeUoM}`;
    }
  }

  return "";
}
